import type { Issue } from '../types/issue';
import { isDone } from './status';

// Bei diesem Kunden werden Kunden-Tickets über das JIRA-Custom-Field
// „CPS Ticket ID" mit dem Support-System (Werte wie „SD-23008647")
// verknüpft. Ein Bug gilt als kundengetrieben, wenn dieses Feld einen
// nicht-leeren Wert hat.
const CPS_FIELD = 'Custom field (CPS Ticket ID)';

export function cpsTicketId(row: Issue): string | null {
  const v = (row as Record<string, unknown>)[CPS_FIELD];
  if (v == null || v === '') return null;
  const val = Array.isArray(v) ? (v[0] ?? '') : v;
  const s = String(val).trim();
  return s || null;
}

export function isCustomerBug(row: Issue): boolean {
  return row['Issue Type'] === 'Bug' && cpsTicketId(row) != null;
}

export type CustomerBugStats = {
  totalBugs:     number;
  customerBugs:  number;
  internalBugs:  number;
  openCustomer:  number;
  openInternal:  number;
  doneCustomer:  number;
  doneInternal:  number;
  perTeam:       PerTeamStat[];
};

export type PerTeamStat = {
  team:      string;
  bugs:      number;    // alle Bugs des Teams
  customer:  number;    // davon Kunden-Bugs
  internal:  number;    // davon interne Bugs
  openCust:  number;
  openInt:   number;
  ratio:     number;    // customer / bugs (0..1)
};

export function computeCustomerBugStats(rows: Issue[]): CustomerBugStats {
  let totalBugs = 0, customerBugs = 0, internalBugs = 0;
  let openCustomer = 0, openInternal = 0, doneCustomer = 0, doneInternal = 0;
  const teamMap = new Map<string, PerTeamStat>();

  for (const r of rows) {
    if (r['Issue Type'] !== 'Bug') continue;
    totalBugs++;
    const status = String(r['Status'] ?? '');
    const done = isDone(status);
    const isCust = cpsTicketId(r) != null;

    if (isCust) {
      customerBugs++;
      if (done) doneCustomer++; else openCustomer++;
    } else {
      internalBugs++;
      if (done) doneInternal++; else openInternal++;
    }

    const team = (r['Team Name'] ?? '(kein Team)') as string;
    let t = teamMap.get(team);
    if (!t) {
      t = { team, bugs: 0, customer: 0, internal: 0, openCust: 0, openInt: 0, ratio: 0 };
      teamMap.set(team, t);
    }
    t.bugs++;
    if (isCust) {
      t.customer++;
      if (!done) t.openCust++;
    } else {
      t.internal++;
      if (!done) t.openInt++;
    }
  }

  const perTeam: PerTeamStat[] = Array.from(teamMap.values()).map(t => ({
    ...t,
    ratio: t.bugs > 0 ? t.customer / t.bugs : 0,
  }));
  perTeam.sort((a, b) => b.bugs - a.bugs);

  return {
    totalBugs, customerBugs, internalBugs,
    openCustomer, openInternal, doneCustomer, doneInternal,
    perTeam,
  };
}
